/**
 * Pure view model of the Settings page (И8: system status / audit log / users &
 * roles, task T.C5).
 *
 * Framework-free on purpose (the `unit` Playwright project runs in plain Node):
 * the api layers are imported TYPE-ONLY, so nothing here drags `import.meta.env`
 * into the test process. The status / audit / users DTOs are contract-first
 * (#23/#24/#25 do not exist yet — T.B5 lands in parallel).
 *
 * Every timestamp read here is compared against a `now` argument that the caller
 * passes, so a tone never depends on when a test happens to run: the e2e pins
 * the ages, the unit specs pin the literals.
 */

import type { AuditDto, AuditListQuery, UserDto, UserSortState } from '../../api/admin';
import type {
  ContractSyncStatusDto,
  MailStatusDto,
  SnapshotSourcesDto,
  SyncStatusDto,
  SystemStatusDto,
  Unti2035StatusDto,
} from '../../api/system-status';
import type { Role } from '../../auth/roles';
import { asDepartment, type DepartmentBucket, type ManagerDepartment } from '../../departments';
import { formatCount, formatDateTimeMsk } from '../dashboard/format';

/** Absence marker, re-exported so the pages do not reach into dashboard/format. */
export { MISSING } from '../dashboard/format';

// ---------------------------------------------------------------------------
// Thresholds
// ---------------------------------------------------------------------------

/**
 * «Stale» for the ЕПГУ/ЕВИ snapshot freshness (docs/06-tasks.md T.C5: the panel
 * carries "stale" thresholds). A fresh export lands about daily, so silence for
 * a day is worth a yellow, and only silence FOREVER is a red.
 */
export const SNAPSHOT_STALE_MS = 24 * 60 * 60 * 1000;

/** Same threshold for a worker/beat (mail poller 5 min, unti sync 10 min, matcher). */
export const SYNC_STALE_MS = SNAPSHOT_STALE_MS;

/**
 * The mail worker's own cadence: `MAIL_POLL_INTERVAL_MS`, whose default is
 * 300 000 ms (`apps/mail-worker/src/config/worker-config.ts`). The panel cannot
 * read the worker's env, so the default is pinned here — and the threshold below
 * is derived from it, which keeps the two honest about each other.
 */
export const MAIL_WORKER_POLL_MS = 5 * 60 * 1000;

/**
 * T.Z38 (owner-observed on prod 2026-09-14): how long without a poll attempt the
 * mail worker may go before the panel calls it dead. Two poll intervals — one
 * missed beat is a hiccup (a slow IMAP hop, a redeploy), two in a row is a
 * process that is gone or stalled. The worker writes `lastRunAt` on EVERY beat
 * (`WorkerLoopService.beat` → `recordPoll`/`recordPollError`), success or
 * failure, so this is a pulse check, not a result check.
 */
export const MAIL_WORKER_STALE_MS = 2 * MAIL_WORKER_POLL_MS;

// ---------------------------------------------------------------------------
// Tones
// ---------------------------------------------------------------------------

/** Four states, not three: «we do not know» is not green, and it is not red either. */
export type TrafficTone = 'ok' | 'warn' | 'bad' | 'unknown';

export const TONE_LABEL: Record<TrafficTone, string> = {
  ok: 'норма',
  warn: 'внимание',
  bad: 'отказ',
  unknown: 'нет данных',
};

function ageMs(iso: string | null | undefined, now: number): number | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  return now - at;
}

/**
 * Freshness, for anything that reports a single "last time I did this".
 * Never ran → red; a timestamp older than the threshold → yellow; a timestamp
 * from the future is not evidence → red.
 */
export function freshnessTone(
  lastAt: string | null | undefined,
  now: number,
  staleAfterMs: number = SNAPSHOT_STALE_MS,
): TrafficTone {
  const age = ageMs(lastAt, now);
  if (age === null) return 'bad';
  if (age < 0) return 'bad';
  if (age > staleAfterMs) return 'warn';
  return 'ok';
}

/**
 * The mail worker's verdict — the worker's CURRENT state, never its history.
 *
 * T.Z38 (owner-observed on prod 2026-09-14): the panel read «Почтовый воркер —
 * отказ» while the worker was alive and ingesting, because `mail.failedItems`
 * — the count of `mail_item` rows that died, which nothing ever decrements —
 * flipped the light red on its own. A dead letter is a fact about a LETTER (the
 * runbook's reprocess queue), not about the PROCESS, and the two boot-time IMAP
 * race victims of the wave-11 roll would have kept the light red forever.
 *
 * The rules, in the order they are checked:
 *  1. no `mail` block at all → «нет данных» (an older api is not a dead worker);
 *  2. `lastRunAt` missing, in the future, or older than {@link MAIL_WORKER_STALE_MS}
 *     → «отказ»: the pulse is gone (the process is down, crash-looping or stalled);
 *  3. alive, but `lastSuccessAt` is missing or older than the same window →
 *     «отказ»: the beats keep firing and keep failing — exactly "the latest
 *     attempt failed", which is all #23 can tell without exposing the worker's
 *     own `errorCode` (it does not, and the contract is additive-only);
 *  4. alive and succeeding, but `pending > 0` → «внимание»: mail is moving, there
 *     is just a backlog to drain;
 *  5. otherwise → «работает».
 *
 * `failedItems` is deliberately NOT a rule: it is displayed (see
 * {@link statusRows}) and never decides the verdict.
 */
export function mailTone(mail: MailStatusDto | undefined, now: number): TrafficTone {
  if (!mail) return 'unknown';

  const runAge = ageMs(mail.lastRunAt, now);
  if (runAge === null || runAge < 0 || runAge > MAIL_WORKER_STALE_MS) return 'bad';

  const successAge = ageMs(mail.lastSuccessAt, now);
  if (successAge === null || successAge < 0 || successAge > MAIL_WORKER_STALE_MS) return 'bad';

  if (numberOrZero(mail.pending) > 0) return 'warn';
  return 'ok';
}

/**
 * A `SyncState` projection (unti FTP sync, the venue matcher): an error code is
 * a red, never having succeeded is a red, running-but-failing (a fresh
 * `lastSyncAt` with no success) is a yellow, plain silence follows freshness.
 */
export function syncTone(sync: SyncStatusDto | undefined, now: number): TrafficTone {
  if (!sync) return 'unknown';
  if (typeof sync.errorCode === 'string' && sync.errorCode.trim() !== '') return 'bad';
  if (ageMs(sync.lastSyncAt, now) === null) return 'bad';
  if (sync.lastSuccessAt === null || sync.lastSuccessAt === undefined) return 'warn';
  return freshnessTone(sync.lastSuccessAt, now, SYNC_STALE_MS);
}

/**
 * ADDITIVE (v1.9.38, T.U1) — the «Синк УНТИ (API)» verdict, read straight off
 * the `state` the api folds (`Unti2035StatusDto`, the ContractSyncStatusDto
 * shape): the sweep either ran cleanly inside its window (`OK`, green), or it
 * did not (`FAIL`, red — an `errorCode` on record, `UNTI2035_PARTIAL` included,
 * or a pulse gone past two 1-h intervals), or it has never run (`NEVER`, grey —
 * without `UNIVERSITY_2035_API_KEY` the job idles BY DESIGN, so "never ran" is
 * the honest absence of data, not a failure). An absent block (a degraded
 * database, an older api) reads grey too.
 */
export function unti2035Tone(sync: Unti2035StatusDto | undefined): TrafficTone {
  if (!sync) return 'unknown';
  if (sync.state === 'OK') return 'ok';
  if (sync.state === 'FAIL') return 'bad';
  return 'unknown';
}

/** The «Синк УНТИ (API)» row's caption (T.U1) — the section name, next to the FTP one. */
export const UNTI2035_ROW_LABEL = 'Синк УНТИ (API)';

/**
 * ADDITIVE (v1.9.38, T.U1) — the sync's own light, appended right under the FTP
 * «Синхронизация УНТИ»: the verdict comes from the api's `state` fold
 * ({@link unti2035Tone}); the detail line prints the sweep clock and the two
 * LIVE counts (the mirror's size and the «Отказов по УНТИ (API)» fact), which
 * stay visible in EVERY state — the counts are facts about the children, true
 * even while the sweep is red or has never run.
 */
function unti2035Row(sync: Unti2035StatusDto | undefined): StatusRow {
  if (!sync) {
    return {
      key: 'unti2035',
      label: UNTI2035_ROW_LABEL,
      tone: 'unknown',
      detail: MISSING_DETAIL,
    };
  }
  return {
    key: 'unti2035',
    label: UNTI2035_ROW_LABEL,
    tone: unti2035Tone(sync),
    detail: [
      `запуск ${formatDateTimeMsk(sync.lastRunAt)}`,
      `заявлений в зеркале ${formatCount(sync.mirrorTotal)}`,
      `отказов по УНТИ ${formatCount(sync.applicationsDeclined)}`,
    ].join(' · '),
    errorCode: sync.errorCode ?? undefined,
  };
}

/**
 * ADDITIVE (v1.9.22, T.Z106) — the contract sync's verdict, read straight off
 * the `state` the api folds (`ContractSyncStatusDto`). Three states, no yellow:
 * the pass either ran cleanly inside its window (`OK`, green), or it did not
 * (`FAIL`, red — an `errorCode` on record or a pulse gone past two intervals),
 * or it has never run (`NEVER`, grey — in kind the job is idle BY DESIGN until
 * `FUTURE_DB_DSN` is wired, so "never ran" is the honest absence of data, not a
 * failure). An absent block (a degraded database, an older api) reads grey too.
 */
export function contractSyncTone(sync: ContractSyncStatusDto | undefined): TrafficTone {
  if (!sync) return 'unknown';
  if (sync.state === 'OK') return 'ok';
  if (sync.state === 'FAIL') return 'bad';
  return 'unknown';
}

/** ЕПГУ/ЕВИ snapshot freshness (contract #23 `snapshots.{EPGU,EVI}`). */
export function snapshotTone(
  lastSnapshotAt: string | null | undefined,
  now: number,
): TrafficTone {
  return freshnessTone(lastSnapshotAt, now, SNAPSHOT_STALE_MS);
}

/**
 * «A manual upload is sitting in the apply hand-off queue» — zero is the normal
 * state and earns no badge at all, any positive count is a yellow. `undefined`
 * (the additive field T.B5 has not sent) reads as zero: absence is not a warning.
 *
 * T.Z40 — a STALLED apply is the escalation: recent snapshots that missed their
 * apply window entirely. That is «the worker is not draining», not «queued», so
 * it reads danger regardless of how many are merely waiting.
 */
export function pendingAppliesTone(pendingApplies: number | undefined, stalledApplies?: number): Extract<TrafficTone, 'ok' | 'warn' | 'bad'> {
  if (numberOrZero(stalledApplies) > 0) return 'bad';
  return numberOrZero(pendingApplies) > 0 ? 'warn' : 'ok';
}

function numberOrZero(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

// ---------------------------------------------------------------------------
// The panel's rows
// ---------------------------------------------------------------------------

export interface StatusRow {
  /**
   * Stable key — the e2e and the CSS hang off it. T.X2 (owner 2026-09-11): the
   * `db`/`redis` probes are NOT rendered any more — which database and which cache
   * sit behind the api is internal infrastructure, and #23 still answers them.
   */
  key: 'mail' | 'unti' | 'unti2035' | 'matcher' | 'contractSync' | 'EPGU' | 'EVI';
  label: string;
  tone: TrafficTone;
  /**
   * The verdict chip's own wording, when the generic {@link TONE_LABEL} would
   * say the wrong thing. T.Z38: for the worker «норма» became «работает» — the
   * light answers the operator's actual question ("is it running?"), and the
   * counter line below answers "has anything died". Absent → the generic label.
   */
  toneLabel?: string;
  /** One line of numbers, already formatted («последний запуск 09.09 06:00 · …»). */
  detail: string;
  /** Error code worth showing verbatim (unti/matcher). */
  errorCode?: string;
}

/**
 * The mail row's green verdict (T.Z38). A green worker is one that is RUNNING,
 * and «работает» is the word the owner uses for that state — «норма» said
 * nothing about a process.
 */
export const MAIL_OK_LABEL = 'работает';

/** The counter label for `mail.failedItems` — ALL-TIME dead letters, not a day window. */
export const MAIL_FAILED_ITEMS_LABEL = 'ошибок (всего)';

/** The «Синк договоров» row's caption (T.Z106) — the section name, not a machine key. */
export const CONTRACT_SYNC_ROW_LABEL = 'Синк договоров';

/**
 * The three live fact buckets of the «Синк договоров» row (T.Z106) — the same
 * three words #3's `?contract=` filter spells and the dashboard widget (T.Z105)
 * renders, so a number on this panel and a number behind that filter can never
 * disagree about who is in which bucket.
 */
export const CONTRACT_SIGNED_LABEL = 'Подписали';
export const CONTRACT_UNSIGNED_LABEL = 'Не подписали';
export const CONTRACT_NOT_ARRIVED_LABEL = 'Не пришли';
// ADDITIVE (v1.9.24, T.Z111) — the two enrollment buckets join the same detail line,
// the same words the register's pills print.
export const CONTRACT_ENROLLED_LABEL = 'Зачислен';
export const CONTRACT_DECLINED_LABEL = 'Отказано в зачислении';

/**
 * The five lights of И8 a manager can act on, in the order an operator reads them —
 * the worker, the beats and the two snapshot sources. T.X2: no `db`/`api`/`redis`
 * rows; that block is an infra detail and is gone from the panel for every role.
 *
 * T.Z38 — every clock in the block reads МСК ({@link formatDateTimeMsk}): the api
 * sends UTC ISO strings and a manager comparing the panel with an e-mail header
 * was reading a time three hours off. The «МСК» suffix travels with each value,
 * so the zone is never implied.
 */
export function statusRows(status: SystemStatusDto, now: number): StatusRow[] {
  const rows: StatusRow[] = [];

  const mail = status.mail;
  const mailVerdict = mailTone(mail, now);
  rows.push({
    key: 'mail',
    label: 'Почтовый воркер',
    tone: mailVerdict,
    toneLabel: mailVerdict === 'ok' ? MAIL_OK_LABEL : undefined,
    detail: mail
      ? [
          `запуск ${formatDateTimeMsk(mail.lastRunAt)}`,
          `успех ${formatDateTimeMsk(mail.lastSuccessAt)}`,
          `в очереди ${formatCount(mail.pending)}`,
          // All-time dead letters (see MailStatusDto): a counter for the reprocess
          // runbook, never a verdict — T.Z38.
          `${MAIL_FAILED_ITEMS_LABEL} ${formatCount(mail.failedItems)}`,
        ].join(' · ')
      : MISSING_DETAIL,
  });

  const unti = status.unti;
  rows.push({
    key: 'unti',
    label: 'Синхронизация УНТИ (FTP)',
    tone: syncTone(unti, now),
    detail: unti
      ? [
          `синхронизация ${formatDateTimeMsk(unti.lastSyncAt)}`,
          `успех ${formatDateTimeMsk(unti.lastSuccessAt)}`,
          `заявлений ${unti.lastCount === null || unti.lastCount === undefined ? '—' : formatCount(unti.lastCount)}`,
        ].join(' · ')
      : MISSING_DETAIL,
    errorCode: unti?.errorCode ?? undefined,
  });

  // ADDITIVE (v1.9.38, T.U1) — the cat.2035.university sweep's own light, right
  // under the FTP row it shares the «УНТИ» name with (and a different source).
  rows.push(unti2035Row(status.unti2035));

  const matcher = status.matcher;
  rows.push({
    key: 'matcher',
    label: 'Матчер площадок',
    tone: syncTone(matcher, now),
    detail: matcher
      ? [
          `запуск ${formatDateTimeMsk(matcher.lastSyncAt)}`,
          `успех ${formatDateTimeMsk(matcher.lastSuccessAt)}`,
          `обработано ${matcher.lastCount === null || matcher.lastCount === undefined ? '—' : formatCount(matcher.lastCount)}`,
        ].join(' · ')
      : MISSING_DETAIL,
    errorCode: matcher?.errorCode ?? undefined,
  });


  rows.push(contractSyncRow(status.contractSync));
  rows.push(...snapshotRows(status.snapshots, now));
  return rows;
}

/**
 * ADDITIVE (v1.9.22, T.Z106) — the contract sync's own light, appended under the
 * established rows. The verdict comes from the api's `state` fold
 * ({@link contractSyncTone}); the detail line names the pass and prints the
 * three LIVE buckets ({@link CONTRACT_SIGNED_LABEL} /
 * {@link CONTRACT_UNSIGNED_LABEL} / {@link CONTRACT_NOT_ARRIVED_LABEL}, the same
 * words #3's `?contract=` filter spells and the dashboard widget T.Z105 renders),
 * which stay visible in EVERY state — the counts are facts about the children,
 * true even while the sync that writes them is red or has never run.
 */
function contractSyncRow(sync: ContractSyncStatusDto | undefined): StatusRow {
  if (!sync) {
    return {
      key: 'contractSync',
      label: CONTRACT_SYNC_ROW_LABEL,
      tone: 'unknown',
      detail: MISSING_DETAIL,
    };
  }
  return {
    key: 'contractSync',
    label: CONTRACT_SYNC_ROW_LABEL,
    tone: contractSyncTone(sync),
    detail: [
      `запуск ${formatDateTimeMsk(sync.lastRunAt)}`,
      `${CONTRACT_SIGNED_LABEL}: ${formatCount(sync.signed)}`,
      `${CONTRACT_UNSIGNED_LABEL}: ${formatCount(sync.arrivedUnsigned)}`,
      `${CONTRACT_NOT_ARRIVED_LABEL}: ${formatCount(sync.notArrived)}`,
      // ADDITIVE (v1.9.24, T.Z111) — an older api (v1.9.23 and earlier) sends no
      // enrollment counts; they read as 0, never as an absent key.
      `${CONTRACT_ENROLLED_LABEL}: ${formatCount(sync.enrolled ?? 0)}`,
      `${CONTRACT_DECLINED_LABEL}: ${formatCount(sync.declined ?? 0)}`,
    ].join(' · '),
    errorCode: sync.errorCode ?? undefined,
  };
}

function snapshotRows(snapshots: SnapshotSourcesDto | undefined, now: number): StatusRow[] {
  const sources: ReadonlyArray<{
    key: 'EPGU' | 'EVI';
    label: string;
    value: string | null | undefined;
  }> = [
    { key: 'EPGU', label: 'Снапшот ЕПГУ', value: snapshots?.EPGU?.lastSnapshotAt },
    { key: 'EVI', label: 'Снапшот ЕВИ', value: snapshots?.EVI?.lastSnapshotAt },
  ];
  return sources.map((source) => ({
    key: source.key,
    label: source.label,
    tone: snapshotTone(source.value, now),
    // T.V8 — the snapshot freshness badge reads MSK, like the dashboard card.
    detail: `последний снапшот ${formatDateTimeMsk(source.value ?? null)}${
      source.value ? ` (возраст ${ageLabel(ageMs(source.value, now))})` : ''
    }`,
  }));
}

const MISSING_DETAIL = 'данные не пришли';

/** Human age for a detail line; `null` (unknown) collapses into the marker. */
export function ageLabel(age: number | null): string {
  if (age === null || age < 0) return '—';
  if (age < 60_000) return 'меньше минуты';
  const minutes = Math.floor(age / 60_000);
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч ${minutes % 60} мин`;
  const days = Math.floor(hours / 24);
  return `${days} дн ${hours % 24} ч`;
}

// ---------------------------------------------------------------------------
// The summary
// ---------------------------------------------------------------------------

const TONE_SEVERITY: Record<TrafficTone, number> = { ok: 0, unknown: 1, warn: 2, bad: 3 };

/** The worst of the lights — the headline of the panel. */
export function overallTone(tones: ReadonlyArray<TrafficTone>): TrafficTone {
  return tones.reduce<TrafficTone>(
    (worst, tone) => (TONE_SEVERITY[tone] > TONE_SEVERITY[worst] ? tone : worst),
    'ok',
  );
}

export function overallLabel(tone: TrafficTone): string {
  switch (tone) {
    case 'ok':
      return 'Всё в порядке';
    case 'warn':
      return 'Есть замечания';
    case 'bad':
      return 'Есть отказы';
    case 'unknown':
      return 'Данных недостаточно';
  }
}

// ---------------------------------------------------------------------------
// The footer
// ---------------------------------------------------------------------------

/** `12345` → `«3 ч 25 мин»`, `null`/negative → the absence marker. */
export function uptimeLabel(seconds: number | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '—';
  const totalMinutes = Math.floor(seconds / 60);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} дн ${hours} ч`;
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  return `${minutes} мин`;
}

export function versionLabel(version: string | undefined): string {
  return typeof version === 'string' && version.trim() !== '' ? version : '—';
}

// ---------------------------------------------------------------------------
// Unwrapping (#23 / #24 / #25)
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Unwraps the #23 answer. `{ data: SystemStatusDto }` is what the contract
 * sends; a bare `SystemStatusDto` (hand-rolled stub, or an api that drops the
 * envelope) unwraps too, and a shape nobody sent becomes an EMPTY status — never
 * `undefined`, so the panel cannot mistake "unexpected payload" for a crash.
 */
export function parseSystemStatus(body: unknown): SystemStatusDto {
  if (!isRecord(body)) return {};
  const raw = body['data'];
  const payload = raw === undefined ? body : raw;
  return isRecord(payload) ? (payload as SystemStatusDto) : {};
}

/** `meta` of #24, normalised (the api always sends it; a stub may not). */
export interface AuditPageMeta {
  page: number;
  pageSize: number;
  total: number;
}

export interface AuditPage {
  rows: AuditDto[];
  meta: AuditPageMeta;
}

/**
 * Unwraps #24. `{ data: { items: […] }, meta }` is what the contract sends; a
 * bare array and `{ data: […] }` (the docs' shorthand «[AuditDto] with meta»)
 * unwrap too. The page math falls back to what was asked for, so a meta-less
 * stub still paginates.
 */
export function parseAuditPage(body: unknown, query: Pick<AuditListQuery, 'page' | 'pageSize'> = {}): AuditPage {
  const askedPage = query.page ?? 1;
  const askedPageSize = query.pageSize ?? AUDIT_PAGE_SIZE_FALLBACK;

  if (Array.isArray(body)) {
    return { rows: body as AuditDto[], meta: { page: askedPage, pageSize: askedPageSize, total: body.length } };
  }
  if (!isRecord(body)) return { rows: [], meta: { page: askedPage, pageSize: askedPageSize, total: 0 } };

  const raw = body['data'];
  const data = Array.isArray(raw) ? raw : isRecord(raw) ? raw : body;
  const metaRecord = isRecord(body['meta']) ? body['meta'] : undefined;
  const rows = Array.isArray(data)
    ? (data as AuditDto[])
    : isRecord(data) && Array.isArray(data['items'])
      ? (data['items'] as AuditDto[])
      : [];

  const meta: AuditPageMeta = {
    page: readPositiveInt(metaRecord?.['page'], askedPage),
    pageSize: readPositiveInt(metaRecord?.['pageSize'] ?? metaRecord?.['limit'], askedPageSize),
    total: readPositiveInt(metaRecord?.['total'], rows.length),
  };
  return { rows, meta };
}

/** Total pages for the pager — at least one, even for an empty list. */
export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

/** A page that fell out of range (filters shrank the list) snaps back into it. */
export function clampPage(page: number, pages: number): number {
  if (!Number.isFinite(page) || page < 1) return 1;
  return Math.min(page, Math.max(1, pages));
}

const AUDIT_PAGE_SIZE_FALLBACK = 25;

function readPositiveInt(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : fallback;
}

/**
 * Unwraps #25 (the list). `{ data: { users: […] } }` is the contract shape; a
 * bare array (the docs' «users list») and `{ users: […] }` unwrap too. Anything
 * else is an empty list — an unexpected payload must not read as "nobody is
 * registered".
 */
export function parseUsers(body: unknown): UserDto[] {
  if (Array.isArray(body)) return body as UserDto[];
  if (!isRecord(body)) return [];
  const data = isRecord(body['data']) ? body['data'] : body;
  if (Array.isArray(data)) return data as UserDto[];
  if (isRecord(data) && Array.isArray(data['users'])) return data['users'] as UserDto[];
  return [];
}

// ---------------------------------------------------------------------------
// Audit table cells
// ---------------------------------------------------------------------------

/**
 * `meta` as ONE line for the table cell, truncated (the full JSON rides in the
 * `title` attribute). `null`/empty collapse into the marker; keys are sorted so
 * the same row always truncates to the same prefix.
 */
export function compactMeta(meta: Record<string, unknown> | null | undefined, max = 80): string {
  if (meta === null || meta === undefined) return '—';
  if (!isRecord(meta)) return String(meta);
  const keys = Object.keys(meta).sort();
  if (keys.length === 0) return '—';
  const json = JSON.stringify(
    Object.fromEntries(keys.map((key) => [key, meta[key]])),
  );
  if (json.length <= max) return json;
  return `${json.slice(0, Math.max(1, max - 1))}…`;
}

/** «Ada Admin (ada@…)» — the display name with the address in brackets, or `#<id>` when the user is gone. */
export function auditUserLabel(userId: number | null, users: ReadonlyArray<UserDto>): string {
  if (userId === null || userId === undefined) return 'система';
  const user = users.find((candidate) => candidate.id === userId);
  if (!user) return `#${userId}`;
  return user.displayName ? `${user.displayName} (${user.email})` : user.email;
}

// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------

/** «Ada Admin», falling back to the e-mail when no display name was set. */
export function userLabel(user: UserDto): string {
  return user.displayName && user.displayName.trim() !== '' ? user.displayName : user.email;
}

/**
 * The editor's options — the whole `UserRole` enum, in ascending power.
 * SUPPORT (T.Z155) is settable like any other role: an ADMIN GRANTS it here,
 * and what it may then do is the api's business (the read-only gate refuses
 * every write server-side; this UI grows no special SUPPORT mode).
 */
export const ROLE_OPTIONS: ReadonlyArray<Role> = ['VIEWER', 'MANAGER', 'ADMIN', 'SUPPORT'];

/**
 * True when a role change must be refused BEFORE the api does: demoting the only
 * ADMIN leaves the installation without one, and #25 answers that with 409. The
 * client-side check is a courtesy — the server stays the authority.
 */
export function isLastAdminDemotion(
  user: Pick<UserDto, 'id' | 'role'>,
  next: Role,
  users: ReadonlyArray<UserDto>,
): boolean {
  if (user.role !== 'ADMIN' || next === 'ADMIN') return false;
  return users.filter((candidate) => candidate.role === 'ADMIN').length <= 1;
}

// ---------------------------------------------------------------------------
// The users-tab filters and the roles explainer (A10/A11, wave 10, T.Z13)
// ---------------------------------------------------------------------------

/**
 * «Кто есть кто» — the three-line callout over the roster (A11). The sentences
 * are the owner's own wording, kept compact on purpose: this is a legend, not a
 * manual, and every line names the ONE surface the role owns since D21.
 */
export const ROLE_EXPLAINER: ReadonlyArray<{ role: Role; text: string }> = [
  { role: 'ADMIN', text: 'всё: площадки, заявления, отчёты, пользователи.' },
  { role: 'MANAGER', text: 'заявления, площадки, отчёты; может вести онлайн-набор на привязанных площадках.' },
  { role: 'VIEWER', text: 'только кабинет своей площадки (вход кодом с привязанного адреса).' },
  // T.Z155 — the fourth role, the owner's own framing: reads the lot, changes nothing.
  { role: 'SUPPORT', text: 'всё читает — как администратор; ничего не меняет: любое изменение отклоняет сервер.' },
];

/** What the filters hold; `null` = the filter is off («все роли» / «все площадки» / «все отделы»). */
export interface UserListFilters {
  role: Role | null;
  venueId: number | null;
  /** ADDITIVE (T.Z42) — the «Отдел» filter; `null` = every department and no department. */
  department: DepartmentBucket | null;
}

export const EMPTY_USER_FILTERS: UserListFilters = { role: null, venueId: null, department: null };

/** True when at least one filter is on — the empty state and the meta line read it. */
export function hasActiveUserFilters(filters: UserListFilters): boolean {
  return filters.role !== null || filters.venueId !== null || filters.department !== null;
}

/**
 * What the venue filter needs of the #6 directory — only the inverse link sets.
 * Structural on purpose: this module stays free of value-imports from the api
 * layer (the `unit` project runs in plain Node).
 */
export interface VenueLinkSource {
  id: number;
  /** Every manager of the venue (`venue_managers`), since v1.6.1. */
  managerUserIds?: ReadonlyArray<number>;
  /** Every curator of the venue (`venue_curators`), since v1.8.0. */
  curatorUserIds?: ReadonlyArray<number>;
}

/**
 * The user ids linked to ONE venue, both kinds folded — the two halves of the
 * venue filter (A10: «users whose managerUserIds OR curatorUserIds contain it»).
 * An ADMIN may hold either kind, so the fold is role-blind on purpose.
 */
export function userIdsOfVenue(
  venues: ReadonlyArray<VenueLinkSource>,
  venueId: number,
): { manager: number[]; curator: number[] } {
  const venue = venues.find((candidate) => candidate.id === venueId);
  // The api's own id rule is «a positive integer» — junk never becomes a match.
  const known = (ids: ReadonlyArray<number> | undefined): number[] =>
    (ids ?? []).filter((id) => Number.isInteger(id) && id >= 1);
  return { manager: known(venue?.managerUserIds), curator: known(venue?.curatorUserIds) };
}

/**
 * True when the api's directory read is too old to answer the venue filter: NO
 * entry carries either link set. The filter then has nothing to match against,
 * and the page says so instead of showing an empty table that would read as
 * «this venue has no people».
 */
export function venueLinksKnown(venues: ReadonlyArray<VenueLinkSource>): boolean {
  if (venues.length === 0) return false;
  return venues.some((venue) => venue.managerUserIds !== undefined || venue.curatorUserIds !== undefined);
}

/**
 * The roster cut through the three filters. CLIENT-SIDE ON PURPOSE: #25 answers
 * the WHOLE roster in one read (no pagination, no server-side filters — the api
 * has none for this endpoint), so a filter change is a re-slice of the cache,
 * never a second request. Role compares the enum the row carries; the venue
 * filter matches by the LINK sets of the directory read, not by the counts the
 * roster ships (a count cannot name the venue). ADDITIVE (T.Z42): the department
 * filter compares the row's own `department` field — a person cannot be two
 * departments, so the buckets here are just OP / OS / NONE; a `MIXED` bucket
 * exists only for venues.
 */
export function usersMatchingFilters(
  users: ReadonlyArray<UserDto>,
  filters: UserListFilters,
  venues: ReadonlyArray<VenueLinkSource>,
): UserDto[] {
  const role = filters.role;
  const department = filters.department;
  const byDepartment = (user: UserDto): boolean => {
    if (department === null) return true;
    return departmentBucketOf(user) === department;
  };
  if (filters.venueId === null) {
    return users.filter(
      (user) => (role === null || user.role === role) && byDepartment(user),
    );
  }
  const linked = userIdsOfVenue(venues, filters.venueId);
  const manager = new Set(linked.manager);
  const curator = new Set(linked.curator);
  return users.filter(
    (user) =>
      (manager.has(user.id) || curator.has(user.id)) &&
      (role === null || user.role === role) &&
      byDepartment(user),
  );
}

/**
 * ADDITIVE (T.Z42) — a USER row's department bucket. A person holds at most one
 * department, so this is `OP`, `OS` or `NONE`; the venue-side `MIXED` bucket does
 * not exist here (see `../departments` for the join).
 */
export function departmentBucketOf(user: Pick<UserDto, 'department'>): 'OP' | 'OS' | 'NONE' {
  return asDepartment(user.department) ?? 'NONE';
}

// ---------------------------------------------------------------------------
// The user drawer (T.X4) — block, name, and the venues of ONE user
// ---------------------------------------------------------------------------

/** `blocked` absent (an older api, a hand-rolled stub) reads as NOT blocked. */
export function isBlocked(user: Pick<UserDto, 'blocked'>): boolean {
  return user.blocked === true;
}

/** The «Площадок» cell: the count, or 0 when an older api omitted the field. */
export function managedVenueCount(user: Pick<UserDto, 'managedVenueCount'>): number {
  return typeof user.managedVenueCount === 'number' && Number.isFinite(user.managedVenueCount)
    ? Math.max(0, user.managedVenueCount)
    : 0;
}

const BLOCKED_BADGE_LABEL = 'заблокирован';

/** The chip on the row and in the drawer — present only while the block stands. */
export function blockedLabel(user: Pick<UserDto, 'blocked'>): string | null {
  return isBlocked(user) ? BLOCKED_BADGE_LABEL : null;
}

/** The block button's caption: the action the next click performs, never the state. */
export function blockActionLabel(user: Pick<UserDto, 'blocked'>): string {
  return isBlocked(user) ? 'Разблокировать' : 'Заблокировать';
}

/**
 * True when a BLOCK must be refused BEFORE the api does — the mirror of
 * {@link isLastAdminDemotion}: blocking the only working ADMIN locks the
 * installation out of the very screen that could undo it, and the api answers
 * that with the same 409. Self-block is refused separately (400 on the api, and
 * here, where the row can be recognised by e-mail — see `isMe`).
 */
export function isLastAdminBlock(
  user: Pick<UserDto, 'id' | 'role' | 'blocked'>,
  users: ReadonlyArray<UserDto>,
): boolean {
  if (user.role !== 'ADMIN' || isBlocked(user)) return false;
  return users.filter((candidate) => candidate.role === 'ADMIN' && !isBlocked(candidate)).length <= 1;
}

export const LAST_ADMIN_BLOCK_HINT = 'Последнего администратора нельзя заблокировать — сначала назначьте другого.';

/** The table row and the drawer both surface this one sentence for the api's 409. */
export const LAST_ADMIN_DEMOTION_HINT = 'Последнего администратора нельзя понизить — сначала назначьте другого.';

/** The roster is the only surface that can unblock, so a self-block is refused on both. */
export const OWN_ACCOUNT_HINT = 'Свой аккаунт заблокировать нельзя — попросите другого администратора.';

/**
 * "Это вы" — matched by e-mail on purpose: `/auth/me` carries the Yandex profile
 * id, which is NOT the `AppUser.id` the users table is keyed by; the e-mail is
 * the one identity both sides share (lower-cased on both).
 */
export function isMe(sessionEmail: string | undefined, rowEmail: string): boolean {
  if (typeof sessionEmail !== 'string' || sessionEmail === '') return false;
  return sessionEmail.trim().toLowerCase() === rowEmail.trim().toLowerCase();
}

/** An e-mail is a NAME-shaped value or nothing: a drawer must not submit prose whitespace. */
export function normalizeDisplayName(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed.slice(0, 200);
}

/**
 * The venue rows of the drawer, minus the one a remove × targets. The list is the
 * drawer's own optimistic state; the PUT that follows carries the WHOLE remaining
 * set, which is what makes the write a replace-set.
 */
export function withoutVenue(
  venues: ReadonlyArray<{ venueId: number; venueName: string }>,
  venueId: number,
): Array<{ venueId: number; venueName: string }> {
  return venues.filter((venue) => venue.venueId !== venueId);
}

/**
 * The venue the combobox commits, already merged: appended when new, ignored when
 * the user manages it already (a duplicate add is a no-op, not a second row).
 * Name-first ordering — the same order #6 answers in, and the order a list of
 * places is read in.
 */
export function withVenue(
  venues: ReadonlyArray<{ venueId: number; venueName: string }>,
  added: { venueId: number; venueName: string },
): Array<{ venueId: number; venueName: string }> {
  if (venues.some((venue) => venue.venueId === added.venueId)) return [...venues];
  return [...venues, added].sort((a, b) => a.venueName.localeCompare(b.venueName, 'ru'));
}

/** The ids a replace-set PUT carries for this list — the whole set, ascending. */
export function venueIdsOf(venues: ReadonlyArray<{ venueId: number }>): number[] {
  return venues.map((venue) => venue.venueId).sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// ADDITIVE wave 9 (v1.8.0, T.Y9) — provider badges, the curator count, #25.1 create
// ---------------------------------------------------------------------------

/** The curator column of the roster: `count(venue_curators)`, 0 when an older api omitted it. */
export function curatorVenueCount(user: Pick<UserDto, 'curatorVenueCount'>): number {
  return typeof user.curatorVenueCount === 'number' && Number.isFinite(user.curatorVenueCount)
    ? Math.max(0, user.curatorVenueCount)
    : 0;
}

// ---------------------------------------------------------------------------
// ADDITIVE (v1.9.53, T.Z177) — the audit count column and its server-side sort
// ---------------------------------------------------------------------------

/**
 * The «Действий в журнале» column: `count(audit_logs where user_id = the row)`.
 * The #25 list read always carries it; absent (an older api) reads as 0 — the
 * column never pretends the api said something it did not.
 */
export function auditActionCount(user: Pick<UserDto, 'auditActions'>): number {
  return typeof user.auditActions === 'number' && Number.isFinite(user.auditActions)
    ? Math.max(0, user.auditActions)
    : 0;
}

/**
 * One sortable column today, so the register's toggle collapses to a flip: the
 * first click sorts ascending (the api's own default), the next flips. `null`
 * means "no parameters" — the api answers the frozen roster order, and the request
 * stays byte-identical to the pre-T.Z177 one.
 */
export function toggleUserSort(current: UserSortState): UserSortState {
  if (current === null) return { sortBy: 'auditActions', order: 'asc' };
  return { sortBy: current.sortBy, order: current.order === 'asc' ? 'desc' : 'asc' };
}

/** The arrow the header prints: the active direction, `null` while unsorted. */
export function userSortArrow(sort: UserSortState): 'asc' | 'desc' | null {
  return sort === null ? null : sort.order;
}

/* A13 (wave 10, D21): the sign-in provider is NOT displayed — every account signs in
   both ways, so a per-account provider badge said nothing true about the person. */

/** The one sentence the drawer prints under the e-mail field of #25.2 (SR §3.6, BR §5.3). */
export const EMAIL_CHANGE_WARN =
  'Пользователь будет входить по новому адресу. Изменение видно в журнале аудита.';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * E-mail validation shared by #25.1 (create) and #25.2 (edit): one `@`, a dot in
 * the domain, no inner whitespace. The api stores the address lowercased — the
 * normalizer does that here too, so a `MixedCase@Example.сom` is not a second
 * identity of the same person.
 */
export function normalizeEmail(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  return EMAIL_RE.test(trimmed) ? trimmed : null;
}

export function emailError(value: string): string | undefined {
  if (value.trim() === '') return 'Введите e-mail.';
  if (normalizeEmail(value) === null) return 'E-mail выглядит неполным — нужен адрес вида name@example.ru.';
  return undefined;
}

/** A display name is REQUIRED on #25.1 (unlike the alias of #25.2, which may stay empty). */
export function displayNameError(value: string): string | undefined {
  if (value.trim() === '') return 'Введите отображаемое имя.';
  return undefined;
}

/** The create-form draft: every field a string (or a staged list), exactly as the inputs hold them. */
export interface UserCreateDraft {
  email: string;
  displayName: string;
  /**
   * A14 (wave 10, D21) — `null` is the "no role chosen" state. The radio starts on
   * the default below, so the form only reaches `null` if a future caller forgets
   * to set it — the validator refuses that state rather than guessing a role.
   */
  role: Role | null;
  /** The manager links staged for a МЕНЕДЖЕР («Площадки»), names included for the pre-submit render. */
  managerVenues: ReadonlyArray<{ venueId: number; venueName: string }>;
  /** The curator links staged for a КУРАТОР («Куратор площадок»). */
  curatorVenues: ReadonlyArray<{ venueId: number; venueName: string }>;
  /**
   * ADDITIVE (T.Z42) — the department staged for a МЕНЕДЖЕР; `null` = «— без отдела»
   * (the row is created with NULL, exactly like today's form).
   */
  department: ManagerDepartment | null;
}

/**
 * A14 — the radio starts on МЕНЕДЖЕР: that is the account an operator creates most
 * (a staff seat for a venue), while a КУРАТОР usually comes into being from the
 * venue card (T.Z11's prompt) and an АДМИН is a rare, deliberate grant.
 */
export const USER_CREATE_DEFAULT_ROLE: Role = 'MANAGER';

export function emptyUserCreateDraft(): UserCreateDraft {
  return {
    email: '',
    displayName: '',
    role: USER_CREATE_DEFAULT_ROLE,
    managerVenues: [],
    curatorVenues: [],
    department: null,
  };
}

/** The one label of the manager link set, on the create form and the drawer alike. */
export const USER_MANAGER_VENUES_LABEL = 'Площадки';

/** The one label of the curator link set — the wording the drawer already uses. */
export const USER_CURATOR_VENUES_LABEL = 'Куратор площадок';

/**
 * A14/D21 — a МЕНЕДЖЕР must hold at least one venue: without the link he sees no
 * funnel at all. The api 400s the same shape (`role-links`), this is the courtesy.
 */
export const USER_MANAGER_VENUES_HINT =
  'Менеджеру нужна хотя бы одна площадка — без привязки он не увидит ни заявлений, ни отчётов.';

/** A14/D21 — a КУРАТОР with no venue cannot sign in by code and has no cabinet. */
export const USER_CURATOR_VENUES_HINT =
  'Куратору нужна хотя бы одна площадка — без привязки вход по коду не сработает.';

/** The «role required» sentence — unreachable through the radio, kept for the model's contract. */
export const USER_ROLE_REQUIRED_HINT = 'Выберите роль.';

/** What {@link userCreateFrom} answers: the body, or the field errors that stopped it. */
export interface UserCreateResult {
  /** `null` when a field is wrong — nothing leaves the page. */
  input: {
    email: string;
    displayName: string;
    role: Role;
    venueIds: number[];
    curatorVenueIds: number[];
    /** ADDITIVE (T.Z42) — a МЕНЕДЖЕР-only fact; `null` = «— без отдела». */
    department: ManagerDepartment | null;
  } | null;
  errors: { email?: string; displayName?: string; role?: string; venues?: string };
}

/**
 * The #25.1 body of a draft, or the field errors that stop it. All fields are
 * checked at once, so one submit shows every problem, not the first one. The api
 * 400s an unknown venue id and a role/link mismatch, and 409s a duplicate address
 * — those stay server-side refusals; everything cheap enough to check before a
 * round trip is checked here. Per D21 the body carries ONLY the link set the role
 * allows (an АДМИН is created with none: its links are set later from the venue).
 */
export function userCreateFrom(draft: UserCreateDraft): UserCreateResult {
  const errors: { email?: string; displayName?: string; role?: string; venues?: string } = {
    email: emailError(draft.email),
    displayName: displayNameError(draft.displayName),
    role: draft.role === null ? USER_ROLE_REQUIRED_HINT : undefined,
  };

  const role = draft.role;
  if (role === 'MANAGER') {
    if (draft.managerVenues.length === 0) errors.venues = USER_MANAGER_VENUES_HINT;
  } else if (role === 'VIEWER') {
    if (draft.curatorVenues.length === 0) errors.venues = USER_CURATOR_VENUES_HINT;
  }

  const stopped = Object.values(errors).some((message) => message !== undefined);
  if (stopped || role === null) return { input: null, errors };

  return {
    input: {
      email: normalizeEmail(draft.email) as string,
      displayName: draft.displayName.trim().slice(0, 200),
      role,
      // The crossed set is ALWAYS empty: the api refuses a crossed pairing, and the
      // form shows one picker — the one the role allows.
      venueIds: role === 'MANAGER' ? venueIdsOf(draft.managerVenues) : [],
      curatorVenueIds: role === 'VIEWER' ? venueIdsOf(draft.curatorVenues) : [],
      // ADDITIVE (T.Z42) — a department travels ONLY on a МЕНЕДЖЕР body: the api 400s
      // one on a КУРАТОР or an АДМИН, so the form never sends it for another role
      // (an АДМИН staged one before switching the radio must not leak it).
      department: role === 'MANAGER' ? asDepartment(draft.department) : null,
    },
    errors: {},
  };
}

/**
 * The #25.2 body of an edited profile: only the keys that actually moved travel,
 * because the api refuses an empty body and an unchanged one would write an
 * audit row for nothing.
 *
 * ADDITIVE (T.Z42) — `department` joins the body when it moved: `null` clears it,
 * a code sets it. The caller passes the row's CURRENT department so a select that
 * was merely mounted does not become a write.
 */
export function userProfilePatchFrom(
  current: Pick<UserDto, 'email' | 'displayName' | 'department'>,
  draft: { email: string; displayName: string; department: ManagerDepartment | null },
): { email?: string; displayName?: string | null; department?: ManagerDepartment | null } {
  const patch: {
    email?: string;
    displayName?: string | null;
    department?: ManagerDepartment | null;
  } = {};
  const email = normalizeEmail(draft.email);
  if (email !== null && email !== current.email.toLowerCase()) patch.email = email;
  const name = draft.displayName.trim();
  if (name !== (current.displayName ?? '')) patch.displayName = name === '' ? null : name.slice(0, 200);
  const department = asDepartment(draft.department);
  if (department !== asDepartment(current.department)) patch.department = department;
  return patch;
}

// ---------------------------------------------------------------------------
// The user drawer's ROLE-BASED link editor (T.Z22, wave 11 — A15/A16/A17)
//
// The drawer writes the links PER VENUE through #8 `managerUserIds` /
// `curatorUserIds` replace-sets. That is what fixes A15 for good: the USER id
// goes INTO the venue's link set, never venue-ids-into-a-user-keyed list. The
// math here is pure so the `unit` project can pin it without a DOM.
// ---------------------------------------------------------------------------

/** The two link kinds a drawer section edits — one per role, never both. */
export type VenueLinkKind = 'manager' | 'curator';

/**
 * A16 — the ONE link section a role may see. A МЕНЕДЖЕР holds manager links, a
 * КУРАТОР curator links, an АДМИН neither (the role is not venue-linked; either
 * link kind is set from the venue card if ever needed). The api still refuses a
 * crossed pairing with its own 400 — this only decides what the drawer SHOWS.
 */
export function venueLinkSectionForRole(role: Role): VenueLinkKind | null {
  if (role === 'MANAGER') return 'manager';
  if (role === 'VIEWER') return 'curator';
  return null;
}

/** The section heading — the same words the create form and the roster use. */
export function venueLinkLabel(kind: VenueLinkKind): string {
  return kind === 'manager' ? USER_MANAGER_VENUES_LABEL : USER_CURATOR_VENUES_LABEL;
}

/** The wire key of #8 the kind writes — the only two replace-sets the venue PATCH takes. */
export function venueLinkPatchKey(kind: VenueLinkKind): 'managerUserIds' | 'curatorUserIds' {
  return kind === 'manager' ? 'managerUserIds' : 'curatorUserIds';
}

/**
 * What the drawer's read of `GET /users/:id/venues` says about the CURATOR links —
 * the ids are the truth, the names are best-effort.
 */
export interface CuratorVenuesRead {
  curatorVenueIds?: ReadonlyArray<number>;
  /** ADDITIVE (v1.9.31, T.Z138) — the names the api itself resolved; absent on an older api. */
  curatorVenues?: ReadonlyArray<{ id: number; name: string }>;
}

/**
 * ADDITIVE (v1.9.31, T.Z138) — the saved curator links, chip-shaped, with the name
 * each id resolves through a THREE-TIER chain: the contract field first (the api
 * read carries the names, so the UI does not depend on the #6 directory being
 * complete — T.Z136 only made the directory fetch every page, which is not a
 * guarantee), the directory second, and «Площадка N» as the last resort. The ids
 * always come from `curatorVenueIds` — the link list itself never changes shape —
 * so a dangling id keeps its row (and, if nobody names it, the fallback label).
 */
export function curatorSavedLinks(
  read: CuratorVenuesRead | undefined,
  directory: ReadonlyArray<{ id: number; name: string }>,
): ReadonlyArray<{ venueId: number; venueName: string }> {
  const names = new Map((read?.curatorVenues ?? []).map((venue) => [venue.id, venue.name]));
  return (read?.curatorVenueIds ?? []).map((venueId) => ({
    venueId,
    venueName: names.get(venueId) ?? directory.find((venue) => venue.id === venueId)?.name ?? `Площадка ${venueId}`,
  }));
}

/** The set with `userId` on it — deduplicated and ascending, the shape #8 stores. */
export function withLinkedUser(ids: ReadonlyArray<number> | undefined, userId: number): number[] {
  return [...new Set([...(ids ?? []), userId])].sort((a, b) => a - b);
}

/** The set with `userId` taken off — everyone else keeps their link, ascending. */
export function withoutLinkedUser(ids: ReadonlyArray<number> | undefined, userId: number): number[] {
  return [...new Set((ids ?? []).filter((id) => id !== userId))].sort((a, b) => a - b);
}

/** What a planned write does to THIS user's membership of one venue. */
export type VenueLinkIntent = 'add' | 'remove';

/** One per-venue write a save runs: the venue it targets and the #8 body it carries. */
export interface VenueLinkPatch {
  venueId: number;
  venueName: string;
  intent: VenueLinkIntent;
  body: { managerUserIds?: number[] } & { curatorUserIds?: number[] };
}

/** What the planner answers: the writes, and the venues it refused to guess about. */
export interface VenueLinkPlan {
  /** The per-venue #8 writes, ascending by venue id. */
  patches: VenueLinkPatch[];
  /**
   * T.Z30 — the changed venues whose CURRENT set of this kind the fresh directory
   * does NOT carry (`undefined`, which is not the same fact as `[]` = "nobody").
   * A payload built without the real set would restate the venue's links (an empty
   * array unlinks EVERYONE), so these are never written — the drawer stops the save
   * and says so instead of shipping a destructive guess as a silent no-op.
   */
  unresolved: Array<{ venueId: number; venueName: string; intent: VenueLinkIntent }>;
}

/** What the planner reads: the kind, the user, the directory, and the two id sets. */
export interface VenueLinkPlanInput {
  kind: VenueLinkKind;
  userId: number;
  /** The #6 directory — fresh at save time, it is where the per-venue sets come from. */
  directory: ReadonlyArray<VenueLinkSource & { name: string }>;
  /** The saved venue ids of THIS user (the drawer's read). */
  savedIds: ReadonlyArray<number>;
  /** The staged venue ids the save must land. */
  stagedIds: ReadonlyArray<number>;
}

/**
 * The per-venue #8 writes a staged set turns into. Only the CHANGED venues
 * travel: an ADDED venue gets its link set with this user appended, a REMOVED
 * one with this user dropped — every other manager/curator of that venue keeps
 * their link, because the set is read from the directory payload, not rebuilt
 * from the drawer's chip list. Ascending by venue id, so a multi-venue save
 * writes in one deterministic order. A changed venue whose current set the
 * directory does not carry goes to {@link VenueLinkPlan.unresolved} instead of
 * producing a body: without its current set the only safe move is no write at all.
 */
export function venueLinkPatchPlan(input: VenueLinkPlanInput): VenueLinkPlan {
  const saved = new Set(input.savedIds);
  const staged = new Set(input.stagedIds);
  const patches: VenueLinkPatch[] = [];
  const unresolved: VenueLinkPlan['unresolved'] = [];

  /** The entry's set of this kind, with "the field is absent" kept apart from "empty". */
  const currentSet = (
    venue: (VenueLinkSource & { name: string }) | undefined,
  ): { known: true; ids: ReadonlyArray<number> } | { known: false; ids: [] } => {
    if (venue === undefined) return { known: false, ids: [] };
    const ids = input.kind === 'manager' ? venue.managerUserIds : venue.curatorUserIds;
    return ids === undefined ? { known: false, ids: [] } : { known: true, ids };
  };

  for (const venueId of input.stagedIds) {
    if (saved.has(venueId)) continue;
    const venue = input.directory.find((entry) => entry.id === venueId);
    const set = currentSet(venue);
    if (!set.known) {
      unresolved.push({ venueId, venueName: venue?.name ?? `Площадка ${venueId}`, intent: 'add' });
      continue;
    }
    patches.push({
      venueId,
      venueName: venue?.name ?? `Площадка ${venueId}`,
      intent: 'add',
      body: { [venueLinkPatchKey(input.kind)]: withLinkedUser(set.ids, input.userId) },
    });
  }
  for (const venueId of input.savedIds) {
    if (staged.has(venueId)) continue;
    const venue = input.directory.find((entry) => entry.id === venueId);
    const set = currentSet(venue);
    if (!set.known) {
      unresolved.push({ venueId, venueName: venue?.name ?? `Площадка ${venueId}`, intent: 'remove' });
      continue;
    }
    patches.push({
      venueId,
      venueName: venue?.name ?? `Площадка ${venueId}`,
      intent: 'remove',
      body: { [venueLinkPatchKey(input.kind)]: withoutLinkedUser(set.ids, input.userId) },
    });
  }
  return {
    patches: patches.sort((a, b) => a.venueId - b.venueId),
    unresolved: unresolved.sort((a, b) => a.venueId - b.venueId),
  };
}

/**
 * The draft a PARTIAL failure leaves (T.Z22): the refetched read is the truth,
 * and the refused intents stay staged on top of it — a refused add keeps its
 * chip, a refused removal keeps its venue OFF the draft, and every applied
 * change is simply gone from the diff. That is what makes «Сохранить» a retry
 * that writes only what is still missing.
 */
export function venueLinkDraftAfterFailure(
  saved: ReadonlyArray<{ venueId: number; venueName: string }>,
  refused: ReadonlyArray<{ venueId: number; venueName: string; intent: VenueLinkIntent }>,
): Array<{ venueId: number; venueName: string }> {
  const refusedRemoves = new Set(
    refused.filter((item) => item.intent === 'remove').map((item) => item.venueId),
  );
  const draft = saved.filter((venue) => !refusedRemoves.has(venue.venueId));
  const present = new Set(draft.map((venue) => venue.venueId));
  for (const item of refused) {
    if (item.intent === 'add' && !present.has(item.venueId)) {
      draft.push({ venueId: item.venueId, venueName: item.venueName });
      present.add(item.venueId);
    }
  }
  return draft;
}

/** True when the staged set differs from the saved one — the save button reads this. */
export function venueLinkIsDirty(
  savedIds: ReadonlyArray<number>,
  stagedIds: ReadonlyArray<number>,
): boolean {
  if (savedIds.length !== stagedIds.length) return true;
  const saved = new Set(savedIds);
  return stagedIds.some((id) => !saved.has(id));
}

/** The live counter of a multi-venue save («Сохраняем 2 из 3…»). */
export function venueLinkProgressLabel(done: number, total: number): string {
  return `Сохраняем ${Math.max(0, done)} из ${Math.max(1, total)}…`;
}

/** The partial-failure sentence — names the venues that refused, promises the rest landed. */
export function venueLinkFailureLabel(
  failed: ReadonlyArray<{ venueName: string }>,
): string {
  const names = failed.map((venue) => `«${venue.venueName}»`).join(', ');
  return `Не удалось сохранить привязки: ${names}. Остальные изменения применены — исправьте и нажмите «Сохранить» снова.`;
}
